import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AdminLayout from "../layouts/AdminLayout";
import FormSelect from "../components/common/FormSelect";
import {
  useApproveAndAssignInstructorMutation,
  useGetAllDemoSessionRequestsQuery,
} from "../services/demo-session/demoSessionApi";
import { useCreateCourseSessionMutation } from "../services/enrollment/enrollmentApi";
import { useGetInstructorsAssignedToACourseQuery } from "../services/course/courseApi";
import {
  ArrowLeft,
  CalendarLineIcon,
  ClockLineIcon,
  Trash,
  UserLineIcon,
} from "../components/AdminIcons";

const SELECT_INSTRUCTOR_OPTION = {
  value: "",
  label: "Select Instructor",
};

const REQUEST_TABS = [
  "Students Demo Requests",
  "Students Live Session Requests",
  "Student Name Change Request",
];

const SESSION_TABS = ["Upcoming Sessions", "Previous Sessions"];
const SESSIONS_PAGE_SIZE = 4;

const SessionMeta = ({ icon: Icon, value, className }) => (
  <div className="flex items-center gap-1.5 text-sm font-medium leading-[21px] text-black sm:gap-2.5 sm:text-lg">
    <Icon className={className} />
    <span>{value}</span>
  </div>
);

export const SessionCard = ({ session, showTrash = false, onDelete }) => (
  <article className="rounded-[19px] border border-[#D9D9D9] bg-white px-4 pb-4 pt-5 sm:px-5 lg:px-7 lg:pb-6 lg:pt-7">
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0 flex-1">
        <h4 className="text-xl sm:text-2xl font-medium leading-6 sm:leading-7 text-black">
          {session.title}
        </h4>
        <p className="mt-[5px]  sm:text-lg leading-[21px] text-[#938A96B2]">
          {session.category}
        </p>
        <div className="mt-5 flex flex-wrap items-center gap-4 sm:mt-6 sm:gap-[26px]">
          <SessionMeta
            className="size-4 sm:size-6"
            icon={UserLineIcon}
            value={session.instructor}
          />
          <SessionMeta
            className="size-4 sm:size-6"
            icon={CalendarLineIcon}
            value={session.date}
          />
          <SessionMeta
            className="size-4 sm:size-6"
            icon={ClockLineIcon}
            value={session.time}
          />
        </div>
      </div>

      {showTrash ? (
        <button
          type="button"
          aria-label={`Delete ${session.title}`}
          onClick={() => onDelete?.(session)}
          className="shrink-0 rounded p-1  sm:mr-6 lg:mr-10 2xl:mr-16 transition"
        >
          <Trash className="text-black hover:text-red-500" />
        </button>
      ) : null}
    </div>
  </article>
);
const DetailField = ({ label, value }) => (
  <div>
    <p className="text-lg sm:text-xl tracking-normal font-medium  sm:leading-[23px]   text-[#9C9C9C]">
      {label}
    </p>
    <p className="mt-1 capitalize  sm:leading-[19px] text-black text-sm sm:text-base">
      {value || "N/A"}
    </p>
  </div>
);

const ReadOnlyField = ({ label, value }) => (
  <label className="block w-full max-w-[370px]">
    <span className="mb-[9px] block leading-[19px]  text-black">{label}</span>
    <div className="rounded-lg capitalize   bg-[#E5E7EB] px-4 py-2 text-sm leading-4 text-[#0E0E2C]">
      {value || "N/A"}
    </div>
  </label>
);

const formatDateLabel = (value) => {
  if (!value) return "N/A";
  const dateValue = new Date(value);
  if (Number.isNaN(dateValue.getTime())) return String(value);
  return dateValue.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const getInstructorName = (instructor) => {
  if (!instructor) return "Unassigned";

  const name = [instructor.firstName, instructor.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  return name || instructor.email || "Unassigned";
};

const getStartOfToday = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

const isPreviousSession = (item) => {
  const status = String(item?.status || "").toLowerCase();
  if (
    status === "rejected" ||
    status === "completed" ||
    status === "cancelled"
  ) {
    return true;
  }

  if (!item?.preferredDate) {
    return false;
  }

  const preferredDate = new Date(item.preferredDate);
  if (Number.isNaN(preferredDate.getTime())) {
    return false;
  }

  preferredDate.setHours(0, 0, 0, 0);
  return preferredDate < getStartOfToday();
};

const mapDemoSessionToCard = (item) => ({
  id: item._id || item.id,
  title: item.course?.courseTitle || "Untitled Course",
  category: item.category || item.course?.courseCategory?.[0] || "N/A",
  instructor: getInstructorName(item.instructor),
  date: formatDateLabel(item.preferredDate),
  time: item.preferredTime || "N/A",
  status: item.status || "",
});

const getInitials = (name) =>
  String(name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase() || "N";

const RequestDemoSession = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const initialRequestTab = REQUEST_TABS.includes(location.state?.tab)
    ? location.state.tab
    : REQUEST_TABS[0];
  const [activeTab, setActiveTab] = useState(initialRequestTab);
  const [activeSessionTab, setActiveSessionTab] = useState(SESSION_TABS[0]);
  const [sessionPage, setSessionPage] = useState(1);

  const request = location.state?.request;
  console.log(request);
  const enrollmentType = request?.enrollmentType;
  const studentName = request?.student;
  const email = request?.email;
  const courseCategory = request?.category;
  const subCategory = request?.subcategory;
  const courseName = request?.course;
  const level = request?.courseLevel;
  const ageGroup = request?.ageGroup;
  const preferredTime = request?.preferredTime;
  const requestDate = formatDateLabel(
    request?.requestDate || request?.createdAt || "2025-05-06",
  );

  const [sessionLink, setSessionLink] = useState(
    request?.demoSessionLink || "",
  );
  const [sessionDate, setSessionDate] = useState("");
  const [sessionStartTime, setSessionStartTime] = useState("");
  const [sessionEndTime, setSessionEndTime] = useState("");
  const isLiveClassEnrollment = enrollmentType === "Live Classes";
  const avatar = request?.avatar || null;
  const courseId = request?.courseId || "";

  const [selectedInstructor, setSelectedInstructor] = useState("");

  const [modalState, setModalState] = useState({
    isOpen: false,
    type: "success", // "success" or "error"
    message: "",
  });

  const [approveAndAssignInstructor, { isLoading: isSchedulingDemo }] =
    useApproveAndAssignInstructorMutation();

  const [createCourseSession, { isLoading: isSchedulingLiveSession }] =
    useCreateCourseSessionMutation();

  const isLoading = isSchedulingDemo || isSchedulingLiveSession;

  const { data: demoSessionsResponse, isFetching: isFetchingSessions } =
    useGetAllDemoSessionRequestsQuery();

  const {
    data: assignedInstructorsResponse,
    isFetching: isFetchingInstructors,
  } = useGetInstructorsAssignedToACourseQuery(courseId, {
    skip: !courseId,
  });

  const instructorOptions = useMemo(() => {
    const raw = assignedInstructorsResponse?.data ?? [];
    const instructors = Array.isArray(raw) ? raw : [];

    const options = instructors
      .map((instructor) => {
        const name = [instructor?.user?.firstName, instructor?.user?.lastName]
          .filter(Boolean)
          .join(" ")
          .trim();

        return {
          value:
            instructor?.user?.id ||
            instructor?.user?._id ||
            instructor?._id ||
            instructor?.id ||
            "",
          label: name || instructor?.user?.email || "Instructor",
        };
      })
      .filter((option) => option.value);

    return [SELECT_INSTRUCTOR_OPTION, ...options];
  }, [assignedInstructorsResponse]);

  const { upcomingSessions, previousSessions } = useMemo(() => {
    const rows = Array.isArray(demoSessionsResponse?.data)
      ? demoSessionsResponse.data
      : [];

    const upcoming = [];
    const previous = [];

    rows.forEach((item) => {
      const session = mapDemoSessionToCard(item);
      if (!session.id) return;

      if (isPreviousSession(item)) {
        previous.push(session);
      } else {
        upcoming.push(session);
      }
    });

    return {
      upcomingSessions: upcoming,
      previousSessions: previous,
    };
  }, [demoSessionsResponse]);

  const handleScheduleClick = async () => {
    if (!selectedInstructor) {
      setModalState({
        isOpen: true,
        type: "error",
        message: "Please select an instructor",
      });
      return;
    }

    if (isLiveClassEnrollment) {
      if (!sessionDate || !sessionStartTime || !sessionEndTime) {
        setModalState({
          isOpen: true,
          type: "error",
          message: "Please select a date, start time, and end time",
        });
        return;
      }

      try {
        const response = await createCourseSession({
          enrollmentId: request.id,
          instructorId: selectedInstructor,
          sessionDate,
          startTime: sessionStartTime,
          endTime: sessionEndTime,
          sessionLink,
        }).unwrap();

        setModalState({
          isOpen: true,
          type: "success",
          message:
            response?.message || "Live class session scheduled successfully!",
        });
      } catch (error) {
        setModalState({
          isOpen: true,
          type: "error",
          message:
            error?.data?.message || "Failed to schedule live class session",
        });
      }
      return;
    }

    try {
      const response = await approveAndAssignInstructor({
        id: request.id,
        demoSessionLink: sessionLink,
        instructorId: selectedInstructor,
      }).unwrap();

      setModalState({
        isOpen: true,
        type: "success",
        message: response?.message || "Demo session scheduled successfully!",
      });
    } catch (error) {
      setModalState({
        isOpen: true,
        type: "error",
        message: error?.data?.message || "Failed to schedule demo session",
      });
    }
  };

  const handleRequestTabClick = (tab) => {
    setActiveTab(tab);
    navigate("/admin/requests", {
      state: { activeTab: tab },
    });
  };

  const allDisplayedSessions =
    activeSessionTab === SESSION_TABS[0] ? upcomingSessions : previousSessions;

  const sessionTotalPages = Math.max(
    1,
    Math.ceil(allDisplayedSessions.length / SESSIONS_PAGE_SIZE),
  );
  const safeSessionPage = Math.min(sessionPage, sessionTotalPages);
  const displayedSessions = allDisplayedSessions.slice(
    (safeSessionPage - 1) * SESSIONS_PAGE_SIZE,
    safeSessionPage * SESSIONS_PAGE_SIZE,
  );

  useEffect(() => {
    setSessionPage(1);
  }, [activeSessionTab]);

  useEffect(() => {
    if (sessionPage > sessionTotalPages) {
      setSessionPage(sessionTotalPages);
    }
  }, [sessionPage, sessionTotalPages]);

  if (!request) {
    return (
      <AdminLayout>
        <section className="font-['Roboto'] min-h-screen bg-[#D9D9D914] px-6 py-6">
          <div className="mx-auto max-w-[1515px]   bg-white px-6 py-10 text-center ">
            <p className="text-sm font-medium uppercase tracking-[0.22em] text-[#A14AA0]">
              Request details
            </p>
            <h1 className="mt-3 text-3xl font-semibold text-[#202531]">
              Request not found
            </h1>
            <p className="mt-2 text-sm text-[#6B7280]">
              This page expects request data from the requests list.
            </p>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="mt-6 rounded-full border border-[#D8D0DD] bg-white px-5 py-2 text-sm font-medium text-[#374151] transition hover:bg-[#FAF7FC]"
            >
              Back
            </button>
          </div>
        </section>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <section className="min-h-screen bg-[#D9D9D914] px-3 pb-10 pt-4 font-['Roboto'] sm:px-5 2xl:pb-16 2xl:pl-8 2xl:pr-7">
        <div className="rounded bg-white px-4 pb-5 pt-4 sm:pb-6">
          <button
            type="button"
            aria-label="Go back"
            onClick={() => window.history.back()}
          >
            <ArrowLeft />
          </button>

          <div className="mt-5 flex gap-6 overflow-x-auto whitespace-nowrap pl-0 font-medium leading-[19px] sm:flex-wrap sm:gap-11 sm:pl-14">
            {REQUEST_TABS.map((tab) => {
              const isActive = tab === activeTab;

              return (
                <button
                  key={tab}
                  type="button"
                  onClick={() => handleRequestTabClick(tab)}
                  className={`relative shrink-0 pb-3 text-left transition ${isActive ? "text-[#923D8C]" : "text-black hover:text-[#9C4898]"}`}
                >
                  {tab}
                  <span
                    className={`absolute inset-x-0 bottom-1 h-1  ${isActive ? "bg-[#923D8C]" : "bg-transparent"}`}
                  />
                </button>
              );
            })}
          </div>
        </div>
        <div className="mt-6 bg-white px-4 pb-4 pt-6 sm:mt-[34px] sm:px-5 sm:pt-8 2xl:pl-[58px] 2xl:pr-9">
          <div className="flex flex-col items-start justify-between gap-6 lg:flex-row 2xl:gap-10">
            <div className="max-w-[888px] w-full">
              <h1 className="text-xl font-medium text-black leading-[23px]">
                Schedule a Demo Session
              </h1>

              <div className="mt-5 grid grid-cols-1 gap-4 xl:grid-cols-2 xl:gap-4 xl:[&>*:nth-child(even)]:justify-self-end [&>*:nth-child(even)]:w-full 2xl:gap-6">
                <ReadOnlyField label="Course Category" value={courseCategory} />
                <ReadOnlyField label="Level" value={level} />
                <ReadOnlyField label="Sub Category" value={subCategory} />
                <ReadOnlyField label="Age Group" value={ageGroup} />
                <ReadOnlyField label="Course Name" value={courseName} />

                <FormSelect
                  id="request-instructor-select"
                  label="Instructor Name"
                  labelClassName="mb-[9px] leading-[19px]"
                  options={instructorOptions}
                  value={selectedInstructor}
                  emptyByDefault={false}
                  className="w-full max-w-[370px]"
                  controlWrapperClassName="mt-0"
                  selectClassName="h-8 rounded-lg  bg-white px-4 text-sm leading-4 text-[#0E0E2C]"
                  onChange={(value) => setSelectedInstructor(value)}
                  dropdownClassName="left-0 right-auto max-h-[45vh] min-w-full overflow-y-auto rounded-lg border border-[#938A96] bg-white"
                  optionClassName="px-3 py-2 text-sm text-[#1F2937]"
                  placeholder={
                    isFetchingInstructors
                      ? "Loading instructors..."
                      : SELECT_INSTRUCTOR_OPTION.label
                  }
                />

                {isLiveClassEnrollment ? (
                  <div className="block w-full max-w-[370px]">
                    <span className="mb-[9px] block leading-[19px] text-black">
                      Select Date
                    </span>
                    <input
                      type="date"
                      value={sessionDate}
                      onChange={(event) => setSessionDate(event.target.value)}
                      placeholder="Select a date"
                      title="Select date"
                      className="h-8 w-full rounded-lg border border-[#938A96] bg-white px-4 text-sm leading-4 text-[#0E0E2C] focus:outline-none"
                    />
                  </div>
                ) : (
                  <ReadOnlyField label="Select Date" value={requestDate} />
                )}

                <div className="block w-full max-w-[370px]">
                  <span className="mb-[9px] block leading-[19px] text-black">
                    Add Session Link
                  </span>

                  <input
                    type="text"
                    value={sessionLink || ""}
                    onChange={(e) => setSessionLink(e.target.value)}
                    placeholder="Enter session link"
                    title="Session link"
                    className="rounded-lg bg-white border border-[#938A96] px-4 py-2 text-sm leading-4 text-[#0E0E2C] h-8 w-full truncate focus:outline-none"
                  />
                </div>
              </div>
              <div className="flex flex-col justify-between gap-6 xl:flex-row xl:items-end">
                <div className="mt-[22px] flex flex-col gap-3 sm:flex-row sm:items-end">
                  {isLiveClassEnrollment ? (
                    <div className="flex flex-col gap-3 sm:flex-row">
                      <div className="block w-full max-w-[175px]">
                        <span className="mb-[9px] block leading-[19px] text-black">
                          Start Time
                        </span>
                        <input
                          type="time"
                          value={sessionStartTime}
                          onChange={(event) =>
                            setSessionStartTime(event.target.value)
                          }
                          title="Start time"
                          className="h-8 w-full rounded-lg border border-[#938A96] bg-white px-4 text-sm leading-4 text-[#0E0E2C] focus:outline-none"
                        />
                      </div>
                      <div className="block w-full max-w-[175px]">
                        <span className="mb-[9px] block leading-[19px] text-black">
                          End Time
                        </span>
                        <input
                          type="time"
                          value={sessionEndTime}
                          onChange={(event) =>
                            setSessionEndTime(event.target.value)
                          }
                          title="End time"
                          className="h-8 w-full rounded-lg border border-[#938A96] bg-white px-4 text-sm leading-4 text-[#0E0E2C] focus:outline-none"
                        />
                      </div>
                    </div>
                  ) : (
                    <ReadOnlyField label="Time Slot" value={preferredTime} />
                  )}
                </div>
                <div className="grid grid-cols-1 gap-3 sm:flex sm:flex-wrap xl:gap-6">
                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="rounded-lg border border-[#938A96] bg-white px-[28px] py-2.5 text-sm font-medium leading-4 text-[#0E0E2C] transition hover:bg-[#FAF7FC]"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleScheduleClick}
                    disabled={isLoading}
                    className="rounded-lg bg-[#923D8C] px-6 py-2.5 text-sm font-medium leading-4 text-[#FAFCFE] transition hover:bg-[#8D3E8C] disabled:opacity-50"
                  >
                    {isLoading ? "Scheduling..." : "Schedule"}
                  </button>
                </div>
              </div>
            </div>

            <aside className="mt-4 block w-full rounded-2xl bg-[#F1EEEE] p-3 sm:px-[15px] sm:py-[12.5px] lg:max-w-[373px] lg:flex-1">
              <div className="h-full rounded-2xl border border-[#4A4A68] border-opacity-[21%] bg-[#F6F1F2] p-4 sm:pl-6 sm:pr-4 sm:pt-[22.5px] sm:pb-[18px]">
                <div className="flex items-center gap-2  pb-2.5">
                  <div className="flex h-16 w-16 sm:h-[76px] sm:w-[76px] items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-[#F97316] to-[#A855F7] text-xl font-semibold text-white">
                    {avatar ? (
                      <img
                        src={avatar}
                        alt={studentName}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      getInitials(studentName)
                    )}
                  </div>
                  <div className="min-w-0">
                    <h2
                      className="truncate text-2xl font-bold leading-7 text-[#4A4A68] sm:text-3xl 2xl:text-[38px] 2xl:leading-[45px]"
                      title={studentName}
                    >
                      {studentName?.length > 10
                        ? `${studentName.slice(0, 10)}...`
                        : studentName}
                    </h2>
                    <p className="truncate text-sm text-[#938A96]">{email}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-x-2 gap-y-5 sm:grid-cols-2">
                  <DetailField label="Course Category" value={courseCategory} />
                  <DetailField label="Age Group" value={ageGroup} />
                  <DetailField label="Sub Category" value={subCategory} />
                  <DetailField label="Preferred Time" value={preferredTime} />
                  <DetailField label="Course" value={courseName} />
                  <DetailField label="Request Date" value={requestDate} />
                  <DetailField label="Level" value={level} />
                </div>
              </div>
            </aside>
          </div>
        </div>

        {/* session section live and demo */}
        <div className="mt-7 bg-white px-4 pb-7 pt-6 sm:pl-[59px] sm:pr-4">
          <div className="flex gap-5 overflow-x-auto whitespace-nowrap sm:flex-wrap">
            {SESSION_TABS.map((tab) => {
              const isActive = tab === activeSessionTab;

              return (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setActiveSessionTab(tab)}
                  className={`relative shrink-0 pb-1.5 text-left font-medium transition ${
                    isActive
                      ? "text-[#923D8C] text-lg sm:text-[22px] leading-[26px]"
                      : "text-black text-base leading-[19px] hover:text-[#9C4898]"
                  }`}
                >
                  {tab}
                  <span
                    className={`absolute inset-x-0 bottom-0 h-1  ${
                      isActive ? "bg-[#923D8C]" : "bg-transparent"
                    }`}
                  />
                </button>
              );
            })}
          </div>

          <div className="mt-6 max-w-[1384px] space-y-5 sm:mt-[34px]">
            {isFetchingSessions ? (
              <p className="text-base text-[#6B7280]">Loading sessions...</p>
            ) : displayedSessions.length === 0 ? (
              <p className="text-base text-[#6B7280]">
                {activeSessionTab === SESSION_TABS[0]
                  ? "No upcoming sessions found."
                  : "No previous sessions found."}
              </p>
            ) : (
              <>
                {displayedSessions.map((session) => (
                  <SessionCard
                    key={session.id}
                    session={session}
                    showTrash={activeSessionTab === SESSION_TABS[1]}
                  />
                ))}

                <div className="flex flex-col items-center justify-between gap-3 pt-4 sm:flex-row">
                  <button
                    type="button"
                    onClick={() =>
                      setSessionPage((page) => Math.max(1, page - 1))
                    }
                    disabled={safeSessionPage <= 1}
                    className={`rounded-full px-6 py-1 text-sm ${
                      safeSessionPage <= 1
                        ? "cursor-not-allowed bg-[#D9D9D9] text-[#6B7280]"
                        : "bg-[#923D8C] text-white"
                    }`}
                  >
                    Prev
                  </button>

                  <p className="text-base font-medium text-black">
                    Page {safeSessionPage}/{sessionTotalPages}
                  </p>

                  <button
                    type="button"
                    onClick={() =>
                      setSessionPage((page) =>
                        Math.min(sessionTotalPages, page + 1),
                      )
                    }
                    disabled={safeSessionPage >= sessionTotalPages}
                    className={`rounded-full px-6 py-1 text-sm ${
                      safeSessionPage >= sessionTotalPages
                        ? "cursor-not-allowed bg-[#D9D9D9] text-[#6B7280]"
                        : "bg-[#923D8C] text-white"
                    }`}
                  >
                    Next
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {modalState.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-3 py-4">
          <div className="w-full max-w-sm rounded-2xl bg-white px-5 py-8 text-center sm:px-8 sm:py-10">
            <div
              className={`mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full ${
                modalState.type === "success" ? "bg-green-100" : "bg-red-100"
              }`}
            >
              {modalState.type === "success" ? (
                <svg
                  className="h-8 w-8 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              ) : (
                <svg
                  className="h-8 w-8 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              )}
            </div>
            <h2
              className={`text-xl font-semibold ${
                modalState.type === "success"
                  ? "text-green-700"
                  : "text-red-700"
              }`}
            >
              {modalState.type === "success" ? "Success" : "Error"}
            </h2>
            <p className="mt-2 text-gray-700">{modalState.message}</p>
            <button
              type="button"
              onClick={() => {
                setModalState({ ...modalState, isOpen: false });
                if (modalState.type === "success") {
                  navigate(-1);
                }
              }}
              className="mt-6 w-full rounded-lg bg-[#923D8C] px-4 py-2.5 text-sm font-medium text-white transition hover:bg-[#8D3E8C]"
            >
              {modalState.type === "success" ? "OK" : "Got It"}
            </button>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default RequestDemoSession;
